import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { authApi, beginLogout, bumpAuthEpoch, getAuthEpoch, isTransientError } from "../lib/api";

const AuthCtx = createContext(null);

// Persistent login: no idle timers. The session lives until explicit logout,
// revocation/disabled account, or 365 days without use.
const RESUME_CHECK_MIN_MS = 30 * 1000;
const RETRY_MS = 10 * 1000;
// Flag (not a credential) for a logout the server has not confirmed yet.
const PENDING_LOGOUT = "vfm_pending_logout";
const hasPendingLogout = () => { try { return localStorage.getItem(PENDING_LOGOUT) === "1"; } catch { return false; } };
const setPendingLogout = (on) => {
  try { on ? localStorage.setItem(PENDING_LOGOUT, "1") : localStorage.removeItem(PENDING_LOGOUT); } catch { /* storage disabled */ }
};
// Cross-tab signal (no credentials): "logout:<ts>" or "login:<ts>".
const AUTH_SIGNAL = "vfm_auth_signal";
const signalTabs = (kind) => {
  try { localStorage.setItem(AUTH_SIGNAL, `${kind}:${Date.now()}`); } catch { /* storage disabled */ }
};
// Try to complete an unconfirmed logout; true when the server confirmed it.
async function flushPendingLogout() {
  if (!hasPendingLogout()) return true;
  try { await authApi.logout(); setPendingLogout(false); return true; } catch { return false; }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined=loading, null=none, object=auth
  const [unreachable, setUnreachable] = useState(false);
  const lastCheck = useRef(0);
  const refreshMeRef = useRef(null);

  // /auth/me silently refreshes once via the axios interceptor. Only a
  // definitive 401 clears the user; network/5xx keep the current state.
  // Late responses from an older auth epoch are ignored entirely.
  const refreshMe = useCallback(async () => {
    lastCheck.current = Date.now();
    const epoch = getAuthEpoch();
    const stale = () => epoch !== getAuthEpoch();
    if (hasPendingLogout()) {
      // Never restore a session the user chose to end.
      await flushPendingLogout();
      if (stale()) return undefined;
      setUser(null);
      setUnreachable(false);
      return null;
    }
    try {
      const data = await authApi.me();
      if (stale()) return undefined;
      setUser(data);
      setUnreachable(false);
      return data;
    } catch (err) {
      if (stale()) return undefined;
      if (err?.response?.status === 401 && !err.vfmTransient) {
        setUser(null);
        setUnreachable(false);
        return null;
      }
      if (isTransientError(err) || err.vfmTransient) setUnreachable(true);
      return undefined;
    }
  }, []);

  refreshMeRef.current = refreshMe;
  useEffect(() => { refreshMe(); }, [refreshMe]);

  // Re-validate on resume/reopen and when connectivity returns.
  useEffect(() => {
    const check = () => {
      if (document.visibilityState === "hidden") return;
      if (Date.now() - lastCheck.current < RESUME_CHECK_MIN_MS && !unreachable) return;
      refreshMe();
    };
    const onPageShow = (e) => { if (e.persisted) refreshMe(); };
    window.addEventListener("online", refreshMe);
    window.addEventListener("focus", check);
    window.addEventListener("pageshow", onPageShow);
    document.addEventListener("visibilitychange", check);
    return () => {
      window.removeEventListener("online", refreshMe);
      window.removeEventListener("focus", check);
      window.removeEventListener("pageshow", onPageShow);
      document.removeEventListener("visibilitychange", check);
    };
  }, [refreshMe, unreachable]);

  // While the server is unreachable, keep retrying in the background.
  useEffect(() => {
    if (!unreachable) return;
    const t = setInterval(refreshMe, RETRY_MS);
    return () => clearInterval(t);
  }, [unreachable, refreshMe]);

  // Raised by the axios layer only when the refresh credential is
  // definitively invalid (401 from /auth/refresh).
  useEffect(() => {
    const onLost = () => {
      bumpAuthEpoch();
      setUser((cur) => {
        if (cur) {
          try { sessionStorage.setItem("vfm_session_lost", "1"); } catch { /* storage disabled */ }
        }
        return null;
      });
      setUnreachable(false);
    };
    // Another tab logged out (online or offline) or logged in.
    const onStorage = (e) => {
      const loggedOut = (e.key === PENDING_LOGOUT && e.newValue === "1") ||
        (e.key === AUTH_SIGNAL && e.newValue?.startsWith("logout:"));
      if (loggedOut) {
        bumpAuthEpoch();
        setUser(null);
        setUnreachable(false);
      } else if (e.key === AUTH_SIGNAL && e.newValue?.startsWith("login:")) {
        bumpAuthEpoch();
        refreshMeRef.current();
      }
    };
    window.addEventListener("auth:session-lost", onLost);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("auth:session-lost", onLost);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const login = async (username, password) => {
    bumpAuthEpoch();  // account switch: drop any outstanding account-state response
    await flushPendingLogout();
    setPendingLogout(false);
    const data = await authApi.login(username, password);
    bumpAuthEpoch();
    await refreshMe();
    signalTabs("login");
    return data;
  };

  // Server-confirmed logout revokes the session and clears cookies. If the
  // server is unreachable we say so and retry when the app is next online.
  const logout = async () => {
    await beginLogout();
    signalTabs("logout");
    let revoked = false;
    try {
      await authApi.logout();
      revoked = true;
      setPendingLogout(false);
    } catch {
      setPendingLogout(true);
      toast.warning("Kunde inte nå servern. Utloggningen slutförs automatiskt när anslutningen är tillbaka.");
    }
    setUser(null);
    setUnreachable(false);
    return { revoked };
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isAdmin: user?.role === "admin",
    loading: user === undefined,
    unreachable,
    login, logout,
    refreshMe,
  };
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export const useAuth = () => useContext(AuthCtx);
